import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Wordmark } from "./Logo";
import { Magnetic, scrollToId, scrollToTop } from "../../lib/ui-motion";
import { useLang } from "../../i18n";

const sections = [
  { id: "about", key: "about" },
  { id: "approach", key: "approach" },
  { id: "build", key: "build" },
  { id: "projects", key: "projects" },
  { id: "tech", key: "tech" },
];

export const Nav = ({ show }) => {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.header
          initial={{ y: -80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -80, opacity: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="fixed top-0 inset-x-0 z-[80]"
          data-testid="site-nav"
        >
          <div className={`mx-auto max-w-[1400px] px-5 md:px-10 transition-all duration-500 ${scrolled ? "py-3" : "py-5"}`}>
            <div className={`flex items-center justify-between rounded-full transition-all duration-500 ${scrolled ? "glass px-5 py-2.5" : "px-1"}`}>
              <button onClick={scrollToTop} data-testid="nav-logo" data-cursor="hover">
                <Wordmark />
              </button>

              <nav className="hidden md:flex items-center gap-1">
                {sections.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => go(s.id)}
                    data-cursor="hover"
                    data-testid={`nav-link-${s.id}`}
                    className="px-4 py-2 text-sm text-white/60 hover:text-white transition-colors duration-300"
                  >
                    {t.nav[s.key]}
                  </button>
                ))}
              </nav>

              <div className="flex items-center gap-2">
                <button
                  onClick={toggle}
                  data-cursor="hover"
                  data-testid="lang-toggle"
                  className="flex items-center gap-1 rounded-full border border-white/10 px-3 py-1.5 text-xs font-mono uppercase tracking-wider hover:border-flame/40 transition-colors"
                >
                  <span className={lang === "en" ? "text-flame" : "text-white/40"}>EN</span>
                  <span className="text-white/20">/</span>
                  <span className={lang === "pt" ? "text-flame" : "text-white/40"}>PT</span>
                </button>

                <Magnetic className="hidden sm:block">
                  <button
                    onClick={() => go("contact")}
                    data-cursor="hover"
                    data-testid="nav-cta"
                    className="rounded-full bg-flame px-5 py-2 text-sm font-semibold text-black hover:bg-flame-soft transition-colors"
                  >
                    {t.nav.cta}
                  </button>
                </Magnetic>

                <button className="md:hidden p-2" onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle" aria-label="Menu">
                  {open ? <X size={22} /> : <Menu size={22} />}
                </button>
              </div>
            </div>
          </div>

          <AnimatePresence>
            {open && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="md:hidden mx-5 mt-2 glass rounded-2xl overflow-hidden"
              >
                <div className="flex flex-col p-4">
                  {sections.map((s) => (
                    <button key={s.id} onClick={() => go(s.id)} className="py-3 text-left text-white/80 hover:text-flame transition-colors" data-testid={`mobile-nav-${s.id}`}>
                      {t.nav[s.key]}
                    </button>
                  ))}
                  <button onClick={() => go("contact")} className="mt-2 rounded-full bg-flame px-5 py-3 font-semibold text-black" data-testid="mobile-nav-cta">
                    {t.nav.cta}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.header>
      )}
    </AnimatePresence>
  );
};
